import * as React from 'react';
import { withRouter, Link, RouteComponentProps } from 'react-router-dom';
import { Button, Card, Col, Divider, Modal, notification, Row, Table } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import DeliveryMethodsQuery from '../../components/Query/DeliveryMethods';
import DeliveryMethodDetail from './DeliveryMethodDetail';
import { Title } from '../../components/Layout/components';
import NotFoundPage from '../NotFound/NotFound';
import PriceDisplay from '../../components/Others/PriceDisplay';
import { exportRawPriceObject } from '../../helpers/Prices/exportRawPriceObject';

interface IDeliveryMethodsTableProps extends RouteComponentProps {
  domain: string;
  deliveryMethodId?: string;
  currency: GQL.ICurrency;
}

interface IDeliveryMethodsTableState {
  deliveryMethod?: GQL.IDeliveryMethod;
}

class DeliveryMethodsTable extends React.Component<IDeliveryMethodsTableProps, IDeliveryMethodsTableState> {

  constructor(props: IDeliveryMethodsTableProps) {
    super(props);
  }

  public componentDidMount() {
    if ((this.props as any).onCollapse) {
      (this.props as any).onCollapse(false);
    }
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <DeliveryMethodsQuery
            domain={this.props.domain}
          >
            {({
              deliveryMethods,
              createDeliveryMethod,
              updateDeliveryMethod,
              deleteDeliveryMethod
            }: any) => {
              return this.renderData(
                deliveryMethods,
                createDeliveryMethod,
                updateDeliveryMethod,
                deleteDeliveryMethod,
                deliveryMethods.refetch
              );
            }}
          </DeliveryMethodsQuery>
        </Col>
      </Row>
    );
  }

  private renderData(
    deliveryMethods: any,
    createDeliveryMethod: Function,
    updateDeliveryMethod: Function,
    deleteDeliveryMethod: Function,
    refetch: Function
  ) {
    const { currency } = this.props;

    if (deliveryMethods && !deliveryMethods.loading) {

      // formulář
      if (this.props && this.props.deliveryMethodId) {
        const selectedDeliveryMethod = deliveryMethods.deliveryMethods.find((d: GQL.IDeliveryMethod) => d.id === this.props.deliveryMethodId) || undefined;
        if (this.props.deliveryMethodId !== 'new' && !selectedDeliveryMethod) {
          return <NotFoundPage domain={this.props.domain} />
        }
        return (
          <DeliveryMethodDetail
            domain={this.props.domain}
            currency={this.props.currency}
            languages={deliveryMethods.languages}
            deliveryMethod={deliveryMethods.deliveryMethods.find((d: GQL.IDeliveryMethod) => d.id === this.props.deliveryMethodId) || undefined}
            onSave={a => this.upsert(a, updateDeliveryMethod, createDeliveryMethod, refetch)}
            onCancel={() => this.cancel()}
          />
        );
      }

      return (
        <div>
          <Title>Delivery methods</Title>
          <Divider />
          <Link className="ant-btn" to={this.getLink('new')}>Create new delivery method</Link>
          <Table
            rowKey={(record: any) => record.id}
            dataSource={deliveryMethods.deliveryMethods}
            defaultExpandAllRows={true}
            style={{ marginTop: '20px' }}
          >
            <Table.Column title="Title" dataIndex="title" key="title" />
            <Table.Column
              title="Price"
              dataIndex="price"
              key="price"
              align="right"
              render={(u, row: GQL.IDeliveryMethod) => {
                const prices = row.prices 
                  ? row.prices.map((price: any) => {
                      return {
                        currencyId: price.currencyId,
                        priceWithout: price.priceWithout,
                      };
                    }) 
                  : [];

                return <PriceDisplay prices={prices} currency={currency} />;
              }}
            />
            <Table.Column
              title="Priority"
              dataIndex="priority"
              key="priority"
              align="center"
              width={100}
            />
            <Table.Column
              title="Active"
              dataIndex="active"
              key="active"
              width={100}
              align="center"
              render={(u, row: GQL.IPaymentMethod) => {
                return (
                  <span>
                    {row.active ? (
                      <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                    ) : (
                        <CheckOutlined style={{ fontSize: 20 }} />
                      )}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              key="actions"
              width={180}
              align="center"
              render={(u: any, row: any) => this.renderActions(u, row, deleteDeliveryMethod, refetch)}
            />
          </Table>
        </div>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading delivery methods ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(
    u: any,
    deliveryMethod: GQL.IDeliveryMethod,
    deleteDeliveryMethod: Function,
    refetch: Function
  ) {
    return (
      <div>
        <Link className="ant-btn" type={'dashed'} to={this.getLink(deliveryMethod.id)}>
          Edit
        </Link>
        <Divider type={'vertical'} />
        <Button danger={true} onClick={() => this.delete(deliveryMethod, deleteDeliveryMethod, refetch)}>
          Delete
        </Button>
      </div>
    );
  }

  private setCurrentDeliveryMethod(deliveryMethod: any) {
    this.setState({ deliveryMethod });
  }

  private delete(deliveryMethod: GQL.IDeliveryMethod, deleteDeliveryMethod: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${deliveryMethod.title}?`,
      content: (
        <span>
          Are you sure you want to delete delivery method <strong>{deliveryMethod.title}</strong>?
        </span>
      ),
      onOk: () => {
        return deleteDeliveryMethod({
          variables: {
            id: deliveryMethod.id
          }
        }).then(() => refetch());
      }
    });
  }

  private upsert(
    deliveryMethod: GQL.IDeliveryMethod,
    updateDeliveryMethod: Function,
    createDeliveryMethod: Function,
    refetch: Function
  ) {
    if (deliveryMethod !== undefined) {
      const variables = {
        id: deliveryMethod.id,
        deliveryMethod: {
          title: deliveryMethod.title,
          name: deliveryMethod.name,
          code: deliveryMethod.code,
          description: deliveryMethod.description,
          price: deliveryMethod.price || 0,
          prices: deliveryMethod.prices && deliveryMethod.prices.map((price: GQL.IDeliveryMethodPrice) => {
            return {
              currencyId: price.currencyId,
              priceWithout: price.priceWithout,
              fromWeight: price.fromWeight,
              toWeight: price.toWeight,
              active: price.active,
            };
          }),
          pricesByWeight: !!deliveryMethod.pricesByWeight,
          deliveryTimeDays: deliveryMethod.deliveryTimeDays,
          paymentMethodIds: (deliveryMethod as any).paymentMethodIds || [],
          countryIds: (deliveryMethod as any).countryIds || [],
          type: deliveryMethod.type,
          priority: deliveryMethod.priority,
          insurance: !!deliveryMethod.insurance,
          insuranceLimits: deliveryMethod.insuranceLimits && deliveryMethod.insuranceLimits.map((limit: GQL.IDeliveryMethodInsuranceLimit) => {
            return {
              prices: limit.prices.map((price: GQL.IPrice) => {
                return exportRawPriceObject(price);
              }),
              fromPriceOrder: limit.fromPriceOrder ? limit.fromPriceOrder.map((price: GQL.IPrice) => {
                return exportRawPriceObject(price);
              }) : null,
              toPriceOrder: limit.toPriceOrder ? limit.toPriceOrder.map((price: GQL.IPrice) => {
                return exportRawPriceObject(price);
              }) : null,
              PohodaIDS: limit.PohodaIDS || null,
              _iid: (limit as any)._iid,
            };
          }),
          active: !!deliveryMethod.active,
          unknownPrice: !!deliveryMethod.unknownPrice,
          unknownPriceLabel: deliveryMethod.unknownPriceLabel,
          PohodaIDS: deliveryMethod.PohodaIDS,
        }
      };

      if (deliveryMethod.id) {
        return updateDeliveryMethod({ variables })
          .then(() => this.setCurrentDeliveryMethod(null))
          .then(() => refetch())
          .then(() => this.cancel())
          .then(() =>
            notification['success']({
              message: 'Info',
              description: `Delivery method "${variables.deliveryMethod.title}" has been updated.`
            })
          );
      }

      (variables.deliveryMethod as any).domain = this.props.domain;

      createDeliveryMethod({ variables })
        .then(() => this.setCurrentDeliveryMethod(null))
        .then(() => refetch())
        .then(() => this.cancel())
        .then(() =>
          notification['success']({
            message: 'Info',
            description: `Delivery method "${variables.deliveryMethod.title}" has been created.`
          })
        );
    }
  }

  private getLink(recordId: string | null) {
    return `/${this.props.domain}/application/${recordId ? `deliveryMethods/${recordId}` : 'deliveryMethods'}`;
  }

  private cancel() {
    this.props.history.push(this.getLink(null));
  }
}

export default withRouter(DeliveryMethodsTable);


// WEBPACK FOOTER //
// ./src/scenes/DeliveryMethods/DeliveryMethodsTable.tsx